// path: frontend/src/pages/Backgrounds.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../lib/firebase";
import { cn } from "../lib/utils";
import { API_BASE_URL } from "../lib/api-config";
import {
  ANIMATED_BACKGROUND_STYLES,
  getBackgroundThumbnail,
  BACKGROUND_TEMPLATES,
} from "../constants/templates";
// Types
type BackgroundTemplate = {
  id: string;
  label: string;
  style: string;
  previewUrl?: string;
  thumbnailUrl?: string;
  source?: "static" | "animated";
};

type FetchState = "idle" | "loading" | "error" | "success";

// User quota/plan info
interface UserQuota {
  plan: "free" | "starter" | "pro";
  features: {
    backgrounds: string;
  };
}

// Helper: Build auth headers
async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (!auth) return headers;

  const user = auth.currentUser;
  if (!user) return headers;

  try {
    const token = await user.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.error("Failed to get auth token:", error);
  }

  return headers;
}

// Convert static templates to BackgroundTemplate format
// 🔒 Now accepts plan parameter to filter by tier
function getStaticTemplates(): BackgroundTemplate[] {
  const allowedTemplates = BACKGROUND_TEMPLATES;

  return allowedTemplates.map((template) => ({
    id: template.id,
    label: template.label,
    style: template.id,
    previewUrl: `${API_BASE_URL}/static/templates/${template.id}`,
    thumbnailUrl: getBackgroundThumbnail(template.id),
    source: "static" as const,
  }));
}

// Fallback: Generate animated templates from static list
function getFallbackAnimatedTemplates(): BackgroundTemplate[] {
  return ANIMATED_BACKGROUND_STYLES.map((style) => ({
    id: style,
    label: style
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
    style: style,
    previewUrl: undefined,
    thumbnailUrl: undefined,
    source: "animated" as const,
  }));
}

// ✅ Get gradient colors based on style name
function getGradientForStyle(style: string): string {
  const gradients: Record<string, string> = {
    med_unified_blue: "from-blue-600 via-blue-500 to-cyan-400",
    neon_waves: "from-pink-500 via-purple-500 to-indigo-600",
    particles_soft: "from-violet-400 via-purple-300 to-indigo-400",
    gradient_pan: "from-orange-400 via-pink-500 to-purple-600",
    med_pathology: "from-red-500 via-pink-500 to-rose-400",
    med_biochemistry: "from-green-500 via-teal-500 to-cyan-500",
    med_physiology: "from-blue-500 via-indigo-500 to-purple-500",
    med_microbiology: "from-emerald-500 via-green-400 to-lime-400",
    med_pharmacology: "from-amber-500 via-orange-500 to-red-500",
    med_biostatistics: "from-slate-500 via-gray-400 to-zinc-400",
    med_public_health: "from-cyan-500 via-blue-500 to-indigo-500",
  };

  return gradients[style] || "from-blue-500 via-purple-500 to-pink-500";
}

export function BackgroundsPage() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const requestedFormat = params.get("format");
  const returnTo = params.get("return") || "/animated-quiz";
  const [templates, setTemplates] = useState<BackgroundTemplate[]>([]);
  const [state, setState] = useState<FetchState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedBg, setSelectedBg] = useState<string | null>(null);

  // 🔒 User plan state
  const [userPlan, setUserPlan] = useState<"free" | "starter" | "pro">("free");
  const [quotaLoading, setQuotaLoading] = useState(false);

  // Load current selection from localStorage
  useEffect(() => {
    const current = localStorage.getItem("selected_bg");
    setSelectedBg(current);
  }, []);

  // 🔒 Fetch user quota to determine plan
  useEffect(() => {
    async function fetchUserQuota() {
      const user = auth?.currentUser;
      if (!user) {
        setUserPlan("free");
        return;
      }

      setQuotaLoading(true);
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${API_BASE_URL}/api/payment/checkQuota?email=${encodeURIComponent(user.email || "")}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.ok) {
          const data: UserQuota = await res.json();
          setUserPlan(data.plan || "free");
          console.log("✅ User plan loaded:", data.plan);
        }
      } catch (error) {
        console.error("Failed to fetch quota:", error);
        setUserPlan("free");
      } finally {
        setQuotaLoading(false);
      }
    }

    fetchUserQuota();
  }, []);

  // Load templates
  useEffect(() => {
    let cancelled = false;

    async function loadTemplates() {
      setState("loading");
      setErrorMessage(null);

      // 🔒 Pass userPlan to filter static templates
      const staticTemplates = getStaticTemplates();

      try {
        const headers = await buildAuthHeaders();
        const url = `${API_BASE_URL}/api/video/background/templates`;

        console.log("🔍 Fetching animated templates from:", url);

        const res = await fetch(url, {
          method: "GET",
          headers,
        });

        if (res.status === 401) {
          if (!cancelled) {
            setTemplates(staticTemplates);
            setState("success");
            setErrorMessage(
              "Sign in to access animated backgrounds. Showing static templates only."
            );
          }
          return;
        }

        if (!res.ok) {
          console.warn(`⚠️ API returned ${res.status}, using static + fallback`);
          if (!cancelled) {
            const fallbackAnimated = getFallbackAnimatedTemplates();
            setTemplates([...staticTemplates, ...fallbackAnimated]);
            setState("success");
            setErrorMessage(
              "Could not load animated templates. Showing static templates and cached animated styles."
            );
          }
          return;
        }

        const data = await res.json();
        console.log("✅ Animated templates loaded:", data);

        if (!cancelled) {
          const animatedList = Array.isArray(data)
            ? data
            : data.templates ?? [];

          // ✅ FIX: Properly extract fields to ensure style is always a string
          const animatedTemplates = animatedList.map((t: any) => ({
            id: t.id || t.style || 'unknown',
            label: t.label || t.name || (typeof t.style === 'string' ? t.style : t.id) || 'Unnamed',
            style: typeof t.style === 'string' ? t.style : (t.style?.name || t.id || 'default'),
            source: "animated" as const,
            thumbnailUrl: undefined,
            previewUrl: t.previewUrl || t.url,
          }));

          const allTemplates = [...staticTemplates, ...animatedTemplates];

          console.log(`📦 Total: ${allTemplates.length} (${staticTemplates.length} static, ${animatedTemplates.length} animated)`);

          setTemplates(allTemplates);
          setState("success");
        }
      } catch (e: any) {
        console.error("Error loading animated templates:", e);
        if (!cancelled) {
          const fallbackAnimated = getFallbackAnimatedTemplates();
          setTemplates([...staticTemplates, ...fallbackAnimated]);
          setState("success");
          setErrorMessage(
            "Could not connect to server. Showing static templates and cached animated styles."
          );
        }
      }
    }

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [userPlan]); // 🔒 Reload when plan changes

  // ✅ Handle selecting a background (for static templates only)
  function handleSelectBackground(template: BackgroundTemplate) {
    try {
      const bgUrl = template.previewUrl || `${API_BASE_URL}/static/templates/${template.id}`;

      localStorage.setItem("selected_bg", bgUrl);
      if (requestedFormat) localStorage.setItem("selected_bg_format", requestedFormat);
      setSelectedBg(bgUrl);

      // Dispatch storage event for other components
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "selected_bg",
          newValue: bgUrl,
        })
      );

      console.log("✅ Background selected:", bgUrl);

      // Navigate back to home
      navigate(returnTo);
    } catch (error) {
      console.error("Failed to select background:", error);
      setErrorMessage("Failed to select background. Please try again.");
    }
  }

  // ✅ Preview handler (for static templates only)
  function handlePreview(template: BackgroundTemplate) {
    if (template.previewUrl) {
      window.open(template.previewUrl, "_blank");
    } else {
      setErrorMessage("Preview URL not available for this template.");
    }
  }

  // Check if template is currently selected
  function isSelected(template: BackgroundTemplate): boolean {
    if (!selectedBg || template.source !== "static") return false;
    const bgUrl = template.previewUrl || `${API_BASE_URL}/static/templates/${template.id}`;
    return selectedBg === bgUrl;
  }

  // Filter templates by requested video format
  const filteredTemplates = templates.filter((t) => {
    if (requestedFormat && t.source !== "static") return false;
    if (requestedFormat && t.source === "static") {
      const id = t.id.toLowerCase();
      if (requestedFormat === "9:16" && !id.includes("vertical")) return false;
      if (requestedFormat === "16:9" && !id.includes("landscape")) return false;
      if (requestedFormat === "1:1" && !id.includes("square")) return false;
    }

    return true;
  });


  return (
    <div className="px-4 py-6 mx-auto max-w-6xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Video Backgrounds</h1>
        <button
          onClick={() => navigate(returnTo)}
          className="text-sm px-3 py-1.5 rounded border hover:bg-black/5 dark:hover:bg-white/10"
        >
          ← Back to Home
        </button>
      </div>

      {/* Info banner */}
      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          🎬 Choose a pre-built MP4 video background that matches your selected video format.
        </p>
      </div>


      {/* Loading state */}
      {(state === "loading" || quotaLoading) && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          <span>Loading templates…</span>
        </div>
      )}

      {/* Error/Warning message */}
      {errorMessage && (
        <div
          className={cn(
            "mt-2 mb-4 p-3 border rounded-lg",
            state === "error"
              ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
              : "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800"
          )}
        >
          <p
            className={cn(
              "text-sm",
              state === "error"
                ? "text-red-800 dark:text-red-200"
                : "text-yellow-800 dark:text-yellow-200"
            )}
          >
            {errorMessage}
          </p>
        </div>
      )}

      {/* Empty state */}
      {state === "success" && filteredTemplates.length === 0 && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No templates in this category.
          </p>
        </div>
      )}

      {/* Templates grid */}
      <div className="grid gap-4 mt-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredTemplates.map((tpl) => (
          <div
            key={tpl.id}
            className={cn(
              "flex flex-col overflow-hidden border rounded-xl shadow-sm bg-white dark:bg-gray-800 hover:shadow-md transition-all",
              isSelected(tpl) && "ring-2 ring-green-500 ring-offset-2"
            )}
          >
            {/* Thumbnail */}
            {tpl.thumbnailUrl ? (
              <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
                <img
                  src={tpl.thumbnailUrl}
                  alt={tpl.label}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                {isSelected(tpl) && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                    ✓ Selected
                  </div>
                )}
              </div>
            ) : (
              // ✅ Animated background placeholder with gradient
              <div className={cn(
                "relative aspect-video bg-gradient-to-br flex items-center justify-center overflow-hidden",
                getGradientForStyle(tpl.style)
              )}>
                {/* Animated shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"
                  style={{
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s infinite'
                  }}
                />

                {/* Icon and label */}
                <div className="relative text-center z-10">
                  <svg
                    className="w-16 h-16 mx-auto mb-2 text-white/90 drop-shadow-lg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xs text-white/90 font-semibold drop-shadow px-2">
                    Dynamic Animation
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex flex-col flex-1 p-4">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                  {tpl.label}
                </h2>
                <span
                  className={cn(
                    "text-xs px-2 py-0.5 rounded-full whitespace-nowrap ml-2",
                    tpl.source === "static"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                      : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                  )}
                >
                  {tpl.source === "static" ? "Pre-built MP4" : "Generated"}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-1">
                {tpl.style}
              </p>

              {/* Action buttons */}
              <div className="mt-auto flex flex-col gap-2">
                {tpl.source === "static" ? (
                  <>
                    {/* Static template - can select directly */}
                    <button
                      type="button"
                      onClick={() => handleSelectBackground(tpl)}
                      disabled={isSelected(tpl)}
                      className={cn(
                        "inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isSelected(tpl)
                          ? "bg-green-600 text-white cursor-default"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      )}
                    >
                      {isSelected(tpl) ? "✓ Currently Selected" : "Use this background"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handlePreview(tpl)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      👁️ Preview
                    </button>
                  </>
                ) : (
                  <>
                    {/* Animated template - go to builder page */}
                    <button
                      type="button"
                      onClick={() => navigate(`/animated-bg?style=${tpl.style}`)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                    >
                      🎨 Customize & Generate →
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add shimmer animation keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

export default BackgroundsPage;